import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { ViewerQuery, ViewerDocument } from "graphql/queries/Viewer.generated";
import { useApolloClient } from "@apollo/client";
import GetHelpModal, { Article, RequestType } from ".";
import { alongEnv, envWithThrowIfMissing } from "utils/env";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import { HelpLinks } from "utils/helpLinks";

type GetHelpProviderProps = {
  children?: React.ReactNode;
};

interface HelpContext {
  getHelp: (options?: {
    helpFormInitiallyExpanded?: boolean;
    subject?: string;
    requestType?: RequestType;
  }) => void;
  setHelpDeskLabel: Dispatch<SetStateAction<string>>;
}

const defaultState = {
  getHelp: () => {},
  setHelpDeskLabel: () => {},
};

const GetHelpContext = createContext<HelpContext>(defaultState);

type CurrentUser = ViewerQuery["viewer"]["currentUser"];

export const useGetHelpContext = () => useContext(GetHelpContext);

async function searchHelpArticleWithTag(
  tag: string,
  setLoadingArticles: Dispatch<SetStateAction<boolean>>,
): Promise<Article[]> {
  try {
    setLoadingArticles(true);

    const configUrl = envWithThrowIfMissing("ZENDESK_CONFIG_URI");
    const apiUrl = `${configUrl}/help_center/en-us/articles.json`;

    const resp = await axios.request({
      method: "GET",
      url: apiUrl,
      params: {
        label_names: tag,
      },
    });

    const helpArticles = resp.data.articles.map(
      (elem: { title: string; html_url: string }) => {
        return {
          title: elem.title,
          link: elem.html_url,
        };
      },
    );

    setLoadingArticles(false);
    return helpArticles;
  } catch (e) {
    setLoadingArticles(false);
    // we don't set the ZENDESK_CONFIG_URI in test and development
    // so lets not send the error to Sentry
    if (alongEnv() === "staging" || alongEnv() === "production") {
      Sentry.captureException(
        new Error("Failed to fetch recommended Zendesk help article: " + e),
      );
    }
    return [];
  }
}

export function GetHelpProvider(props: GetHelpProviderProps) {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [handleRequest, setHandleRequest] = useState(false);
  const [formInitiallyExpanded, setFormInitiallyExpanded] = useState(false);
  const [subject, setSubject] = useState<string | undefined>();
  const [requestType, setRequestType] = useState<RequestType | undefined>();
  const [showModal, setShowModal] = useState(false);
  const client = useApolloClient();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [helpDeskLabel, setHelpDeskLabel] = useState("");

  const getArticles = useCallback(async (label: string) => {
    if (label === "") {
      return null;
    }

    const relevantHelpArticles = await searchHelpArticleWithTag(
      label,
      setLoadingArticles,
    );
    setArticles(relevantHelpArticles);
  }, []);

  useEffect(() => {
    if (handleRequest) {
      setHandleRequest(false);
      if (currentUser) {
        const label =
          currentUser.__typename === "Teacher" && !currentUser.canHaveStudents
            ? "explore_mode"
            : helpDeskLabel;
        getArticles(label);
        setShowModal(true);
      } else {
        window.open(HelpLinks.HELP_DESK, "_blank");
      }
    }
  }, [handleRequest, helpDeskLabel, currentUser, getArticles]);

  const getCurrentUserFromCache = () => {
    try {
      // TODO: the useCurrentUser hook needs to be refactored.
      // We should use a global context to handle the current user
      // state and pass it down. In the meantime, we're attempting
      // to fetch the current user directly from Apollo's cache.
      const data = client.readQuery<ViewerQuery>({
        query: ViewerDocument,
      });
      setCurrentUser(data?.viewer.currentUser || null);
    } catch {
      setCurrentUser(null);
    }
  };

  const getHelp: HelpContext["getHelp"] = ({
    helpFormInitiallyExpanded = false,
    subject,
    requestType,
  } = {}) => {
    getCurrentUserFromCache();
    setFormInitiallyExpanded(helpFormInitiallyExpanded);
    setSubject(subject);
    setRequestType(requestType);
    setHandleRequest(true);
  };

  return (
    <GetHelpContext.Provider value={{ getHelp, setHelpDeskLabel }}>
      {currentUser && (
        <GetHelpModal
          show={showModal}
          userType={currentUser.type}
          name={currentUser.name}
          subject={subject}
          requestType={requestType}
          email={currentUser.email}
          isLoadingArticles={loadingArticles}
          articles={articles}
          helpFormInitiallyExpanded={formInitiallyExpanded}
          onClose={() => setShowModal(false)}
        />
      )}
      {props.children}
    </GetHelpContext.Provider>
  );
}
